import React from "react";
import PropTypes from "prop-types";

const SYSTEM_COLUMNS = new Set([
  "mapping_file_details_id",
  "mapping_file_id",
  "templateid",
  "isactive",
]);

const EDITABLE_COLUMNS = [
  'comments',
  'data cleansing considerations',
  'source field description',
  'view field name',
  'sourcefielddescription',
  'viewfieldname'
];

const NORMALIZED_EDITABLE_COLUMNS = EDITABLE_COLUMNS.map(col =>
  col.toLowerCase().replaceAll(/\s+/g, '').replaceAll(/[-_]/g, '')
);

const CheckboxCell = ({ row, col, updateRowFn }) => {
  const { original, index } = row;
  const value = original[col];
  const isChecked =
    value === true ||
    value === "true" ||
    value === "Yes" ||
    value === "Y" ||
    value === "y" ||
    value === 1 ||
    value === "1";

  return (
    <input
      type="checkbox"
      checked={isChecked}
      onChange={(e) => {
        if (typeof updateRowFn === "function") {
          const newValue = e.target.checked ? "Y" : "N";
          updateRowFn(
            row,
            col,
            newValue,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            (prevData) => {
              const newData = [...prevData];
              newData[index] = { ...original, [col]: newValue };
              return newData;
            }
          );
        }
      }}
      style={{
        width: "16px",
        height: "16px",
        cursor: "pointer",
        margin: "auto",
        display: "block",
      }}
    />
  );
};

CheckboxCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
  }).isRequired,
  col: PropTypes.string.isRequired,
  updateRowFn: PropTypes.func,
};

const createCheckboxCellRenderer = (col, updateRowFn) => {
  const CellRenderer = ({ row }) => (
    <CheckboxCell row={row} col={col} updateRowFn={updateRowFn} />
  );
  CellRenderer.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.object.isRequired,
      index: PropTypes.number.isRequired,
    }).isRequired,
  };
  return CellRenderer;
};

export const processJsonData = ({
  jsonData,
  dropDownData,
  resTemplateData,
  setColumns,
  setData,
  setFieldTypeMap,
  setDropdownOptions,
  setSourceTableColumnName,
  updateRowFn,
}) => {
  if (jsonData.length === 0) return;

  const rawHeaders = Object.keys(jsonData[0]).filter(
    (header) => !SYSTEM_COLUMNS.has(header.toLowerCase())
  );

  const deduplicatedHeaders = [];
  const seenColumnTypes = new Set();

  rawHeaders.forEach((header) => {
    const lowerHeader = header.toLowerCase();
    if (lowerHeader.includes("mandatory")) {
      if (!seenColumnTypes.has("mandatory")) {
        seenColumnTypes.add("mandatory");
        deduplicatedHeaders.push(header);
      }
    } else if (lowerHeader.includes("scope")) {
      if (!seenColumnTypes.has("inscope")) {
        seenColumnTypes.add("inscope");
        deduplicatedHeaders.push(header);
      }
    } else {
      deduplicatedHeaders.push(header);
    }
  });

  const renamedHeaders = deduplicatedHeaders.map(header => {
    if (header.toLowerCase() === 'comments_1') return 'Technical Comments';
    return header;
  });

  const newFieldTypeMap = new Map(
    resTemplateData.map((field) => [field.excelColumnName, field])
  );
  setFieldTypeMap(newFieldTypeMap);
  setDropdownOptions(dropDownData);

  const dataColumns = renamedHeaders.map((col, index) => {
    const originalCol = deduplicatedHeaders[index];
    const fieldConfig = newFieldTypeMap.get(originalCol);

    const isCheckboxField =
      col.toLowerCase().includes("mandatory") ||
      col.toLowerCase().includes("inscope") ||
      col.toLowerCase().includes("in-scope") ||
      col.toLowerCase().includes("in scope") ||
      fieldConfig?.controlType === "Checkbox";

    if (isCheckboxField) {
      return {
        accessorKey: originalCol,
        header: col,
        editType: "checkbox",
        size: 100,
        columncategoryname: fieldConfig?.columncategoryname || "",
        // FIX: must enable global filter so TanStack includes this column
        enableGlobalFilter: true,
        cell: createCheckboxCellRenderer(col, updateRowFn),
      };
    } else if (
      newFieldTypeMap.has(col) &&
      fieldConfig.dbColumnName === "sourcetablename"
    ) {
      setSourceTableColumnName(col);
      return {
        accessorKey: col,
        fieldName: "STN",
        header: col,
        editType: "dropdown",
        options: dropDownData,
        size: 120,
        columncategoryname: fieldConfig?.columncategoryname || "",
        enableGlobalFilter: true,
      };
    } else if (
      newFieldTypeMap.has(col) &&
      fieldConfig.dbColumnName === "sourcefieldname"
    ) {
      return {
        accessorKey: col,
        fieldName: "SFN",
        header: col,
        editType: "dropdown",
        options: [],
        size: 120,
        columncategoryname: fieldConfig?.columncategoryname || "",
        enableGlobalFilter: true,
      };
    } else if (newFieldTypeMap.has(col) && fieldConfig.controlType === "Text") {
      return {
        accessorKey: col,
        header: col,
        editType: "text",
        size: 120,
        columncategoryname: fieldConfig?.columncategoryname || "",
        enableGlobalFilter: true,
      };
    } else {
      const lowerColName = col.toLowerCase().replaceAll(/\s+/g, '').replaceAll(/[-_]/g, '');
      const isEditableColumn = NORMALIZED_EDITABLE_COLUMNS.some(normalizedEditableCol =>
        lowerColName.includes(normalizedEditableCol)
      );

      if (isEditableColumn) {
        return {
          accessorKey: col,
          header: col,
          editType: "text",
          size: 120,
          columncategoryname: fieldConfig?.columncategoryname || "",
          enableGlobalFilter: true,
        };
} else {
        // If this is the Sq.No column, render row sequence number
        if (col.toLowerCase().replace(/[\s._-]/g, '') === 'sqno') {
          return {
            accessorKey: col,
            id: col,
            header: col,
            size: 80,
            columncategoryname: fieldConfig?.columncategoryname || "",
            enableGlobalFilter: false,
            cell: ({ row, table }) => {
              const { pageIndex, pageSize } = table.getState().pagination;
              return pageIndex * pageSize + row.index + 1;
            },
          };
        }
        return {
          accessorKey: col,
          id: col,
          header: col,
          size: 120,
          columncategoryname: fieldConfig?.columncategoryname || "",
          enableGlobalFilter: true,
        };
      }
    }
  });

  setColumns([...dataColumns]);
  setData([...jsonData]);
};

/**
 * FIX: Correct globalFilterFn signature for TanStack Table v8.
 *
 * TanStack calls this ONCE PER COLUMN PER ROW, passing the specific
 * columnId and the cell value for that column. Do NOT call row.getAllCells()
 * inside here — that re-scans every cell on every column call, is O(n²),
 * and returns stale values for columns not yet processed.
 *
 * The correct approach: just check the single cell value passed in.
 * TanStack will OR the results across all columns automatically.
 */
export const globalFilterFn = (row, columnId, filterValue) => {
  if (!filterValue || filterValue === "") return true;
  const search = String(filterValue).toLowerCase().trim();
  // getValue() reads the accessor output for this specific column
  const cellValue = row.getValue(columnId);
  if (cellValue === null || cellValue === undefined) return false;
  return String(cellValue).toLowerCase().includes(search);
};

// Required by TanStack to support auto-remove when filter is cleared
globalFilterFn.autoRemove = (val) => !val || val === "";

export const handleColumnResizeStart = (
  e,
  header,
  setResizingColumn,
  columnSizes,
  setColumnSizes
) => {
  e.preventDefault();
  setResizingColumn(header.id);

  const startX = e.pageX;
  const startWidth = columnSizes[header.id] || header.column.getSize();

  const handleMouseMove = (e) => {
    const newWidth = Math.max(startWidth + (e.pageX - startX), 50);
    setColumnSizes((prev) => ({ ...prev, [header.id]: newWidth }));
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    setResizingColumn(null);
  };

  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", handleMouseUp);
};

export const calculateTableHeight = (pagination) => {
  const headerHeight = 36;
  const rowHeight = 36;
  const desiredRows = pagination.pageSize;
  return headerHeight + rowHeight * desiredRows + "px";
};

export const handlePageSizeChange = (event, pagination, setPagination, table) => {
  const newSize = Number(event.target.value);
  setPagination((prev) => {
    const newPageIndex = Math.floor((prev.pageIndex * prev.pageSize) / newSize);
    return { ...prev, pageSize: newSize, pageIndex: newPageIndex };
  });
  table.setPageSize(newSize);
};

export const updateRow = async ({
  row,
  columnHeader,
  updatedValue,
  fieldName,
  fieldTypeMap,
  dependentValues,
  sourceTableColumnName,
  fetchDependentValues,
  setData,
  setLocalValue,
  updateData
}) => {
  const previousValue = row.original[columnHeader];
  if (updatedValue !== previousValue) {
    let updatedRow = { ...row.original, [columnHeader]: updatedValue };

    if (fieldName === "STN") {
      await fetchDependentValues(updatedValue);
      fieldTypeMap.forEach((fieldConfig) => {
        if (fieldConfig.dbColumnName === "sourcefieldname" || fieldConfig.dbColumnName === "sourcedatatype") {
          updatedRow = { ...updatedRow, [fieldConfig.excelColumnName]: "" };
        }
      });
    } else if (fieldName === "SFN") {
      const selectedColumn = dependentValues[
        row.original[sourceTableColumnName]
      ]?.find((col) => col.columnName === updatedValue);

      if (selectedColumn) {
        fieldTypeMap.forEach((fieldConfig) => {
          let fieldToUpdate = "";
          let fieldToUpdateValue = "";
          if (fieldConfig.dbColumnName === "sourcedatatype") {
            fieldToUpdate = fieldConfig.excelColumnName;
            fieldToUpdateValue = selectedColumn.columnDataType;
          }
          if (fieldToUpdate && fieldToUpdateValue) {
            updatedRow = { ...updatedRow, [fieldToUpdate]: fieldToUpdateValue };
          }
        });
      }
    }

    setData((prevData) => {
      const newData = [...prevData];
      newData[row.index] = updatedRow;
      return newData;
    });

    try {
      await updateData(updatedRow);
    } catch (error) {
      console.error(error);
      setData((prevData) => {
        const newData = [...prevData];
        newData[row.index] = row.original;
        return newData;
      });
    }

    setLocalValue((prev) => {
      const updated = { ...prev };
      delete updated[`${row.index}-${columnHeader}`];
      return updated;
    });
  }
};

export const prepareUpdateData = (rowData, fileID, dbFieldTypeMap) => {
  const postDataTemplate = {
    updateMapperDetailsRequests: [
      {
        userMappingFileDetailsId: 0,
        transformationrule: "string",
        sourcefield: "string",
        crossreferencetable: "string",
        defaultvalue: "string",
        conversionrule: "string",
        reusablelogic: "string",
        criticalerrorhandling: "string",
        comments: "string",
        sourcegroupview: "string",
        sourcetablename: "string",
        sourcefieldname: "string",
        sourcefielddescription: "string",
        sourcedatatype: "string",
        sourcedatalength: "string",
        viewfieldname: "string",
        inscope: "string",
        mandatoryfeild: "string",
        targetstructure: "string",
        fieldname: "string",
        length: "string",
        type: "string",
        decimalscale: 0,
      },
    ],
    userMappingFileId: 0,
  };

  const mappedData = postDataTemplate.updateMapperDetailsRequests.map((row) => {
    const newRow = { userMappingFileDetailsId: rowData.UserMappingFileDetailsId };
    Object.entries(row).forEach(([key]) => {
      if (dbFieldTypeMap.has(key)) {
        newRow[key] = rowData[dbFieldTypeMap.get(key)];
      }
    });
    return newRow;
  });

  return { userMappingFileId: fileID, updateMapperDetailsRequests: mappedData };
};

export const customSelectStyles = {
  control: (provided) => ({ ...provided, minHeight: "28px", height: "28px" }),
  valueContainer: (provided) => ({ ...provided, height: "28px", padding: "0 6px" }),
  input: (provided) => ({ ...provided, margin: "0px" }),
  indicatorsContainer: (provided) => ({ ...provided, height: "28px" }),
  dropdownIndicator: (provided) => ({ ...provided, padding: "4px" }),
  clearIndicator: (provided) => ({ ...provided, padding: "4px" }),
};

export const getDropdownStyles = (additionalStyles = {}) => ({
  ...customSelectStyles,
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (provided) => ({ ...provided, minWidth: "250px", width: "max-content" }),
  control: (provided) => ({
    ...provided,
    width: "100%",
    minHeight: "24px",
    height: "24px",
    fontSize: "12px",
  }),
  valueContainer: (provided) => ({ ...provided, height: "24px", padding: "0 4px" }),
  input: (provided) => ({ ...provided, margin: "0px", padding: "0px" }),
  singleValue: (provided) => ({ ...provided, fontSize: "12px" }),
  indicatorsContainer: (provided) => ({ ...provided, height: "24px" }),
  dropdownIndicator: (provided) => ({ ...provided, padding: "2px" }),
  clearIndicator: (provided) => ({ ...provided, padding: "2px" }),
  option: (provided) => ({
    ...provided,
    fontSize: "12px",
    padding: "4px 8px",
    whiteSpace: "nowrap",
  }),
  ...additionalStyles,
});